import type {ReactElement} from 'react';
import {useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownSelectorCarets} from '@Components/dropdown-v2/components/dropdown-selector-carets/dropdown-selector-carets';
import {isSmallScreenDropdown} from '@Components/dropdown-v2/dropdown.helper';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {Text, TextSize} from '@Components/text';
import {noop} from '@Utils/general.util';
import type {SelectorPillDropdownProps} from './selector-pill-dropdown.types';
import {SELECTOR_PILL_MODAL_SPACING_CLASSES, SELECTOR_PILL_POPUP_OFFSET, SELECTOR_PILL_POPUP_SPACING_CLASSES} from './selector-pill-dropdown.types';
import styles from './selector-pill-dropdown.module.scss';

export function SelectorPillDropdown(props: SelectorPillDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  const closeDropdown = (): void => {
    setIsOpen(false);
  };

  const onDropdownOpen = (): void => {
    // On small screens the dropdown opens the more-options modal instead, which owns its own close lifecycle (X button), so the open state must not stick to the pill.
    if (!isSmallScreenDropdown()) {
      setIsOpen(true);
    }
  };

  const getPill = (): ReactElement => {
    return (
      <ClickableDiv onClick={noop} className={`${styles.pill} ${props.pillClassName}`} aria-label={props.title}>
        {props.leadingVisual}
        <Text val={props.pillText} size={TextSize.SM} bold />
        <DropdownSelectorCarets isOpened={isOpen} size={DROPDOWN_SIZE.XSMALL} />
      </ClickableDiv>
    );
  };

  return (
    <Dropdown
      selector={getPill()}
      popup={props.renderPopupContent({isSmallScreen: false, closeDropdown})}
      open={isOpen}
      onOpen={onDropdownOpen}
      onClose={closeDropdown}
      position={DROPDOWN_POSITION.FIXED}
      alignment={ALIGNMENT_TYPE.BOTTOM_START}
      // The pills scroll with the panel; a FIXED popup either follows them over the editor header or floats detached, so an outer scroll closes it instead.
      closeOnOuterScroll
      popUpHasCustomWidth
      popupClassName={`${styles.popup} ${props.popupClassName}`}
      popupSpacingClass={SELECTOR_PILL_POPUP_SPACING_CLASSES}
      selectorClassNameOnOpen={styles.openedPill}
      popupOffset={SELECTOR_PILL_POPUP_OFFSET}
      showModalForSmallScreen
      isSmallScreenModalBottomSheet
      modalTitle={props.title}
      modalContent={props.renderPopupContent({isSmallScreen: true, closeDropdown})}
      modalClassName={SELECTOR_PILL_MODAL_SPACING_CLASSES}
      modalHeight={props.modalHeight}
      modalMaxHeight={props.modalMaxHeight}
      panelIdForModal={props.panelIdForModal}
    />
  );
}
